import { useEffect } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { CallHistoryPage } from './pages/CallHistoryPage';
import { RequireAuth, RequireAdmin } from './components/RouteGuard';
import { Layout } from './components/Layout';
import { useAuth } from './store/auth';
import { restoreSession } from './auth/cognito';
import { getStoredSessionId } from './auth/sessionId';

export function App() {
  const setSession = useAuth(s => s.setSession);
  const markReady = useAuth(s => s.markReady);

  useEffect(() => {
    restoreSession()
      .then(session => {
        if (session) {
          // FR-66: re-attach the previously-claimed session id from
          // localStorage so X-Session-Id keeps matching what Dynamo
          // holds — silent same-browser refresh path.
          setSession({
            user: session.user,
            idToken: session.idToken,
            accessToken: session.accessToken,
            expiresAt: session.expiresAt,
            sessionId: getStoredSessionId(),
          });
        } else {
          markReady();
        }
      })
      .catch(() => markReady());
  }, [markReady, setSession]);

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route element={<AuthedLayout />}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/history" element={<CallHistoryPage />} />
        <Route path="/admin/users" element={<RequireAdmin><AdminUsersPage /></RequireAdmin>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/**
 * Shared parent for all authenticated routes. Layout mounts once and stays
 * mounted across navigation — useDevice's Voice SDK Device + token-refresh
 * timers + call store all survive Dashboard ↔ History ↔ Users navigation,
 * so an in-progress call doesn't get hung up when the agent clicks around.
 */
function AuthedLayout() {
  return (
    <RequireAuth>
      <Layout>
        <Outlet />
      </Layout>
    </RequireAuth>
  );
}
